<template>
  <div class="flex flex-col bg-white h-full w-full" ref="mainContent">
    <!-- Table Container -->
    <div class="h-full border border-gray-300 rounded-lg shadow-lg overflow-y-hidden">

      <!-- Table -->
      <div class="flex-1 w-full h-full overflow-y-scroll">
        <table class="w-full divide-y divide-gray-200 font-mono text-xs">
          <!-- Table header -->
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="th-class pl-4 w-1/2">Event</th>
              <th class="th-class w-1/4">Chance</th>
              <th class="th-class w-1/4">Trend</th>
            </tr>
          </thead>

          <!-- Table contents / rows -->
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="({ event, market, isParentRow, rowKey }, index) in flattenedEvents" 
                :key="rowKey"
                :id="rowKey" 
                :class="[
                  store.tableState.updatedRowKeys[rowKey] ? 'animate-pulse' : '',
                  'h-12 ml-2'
                ]" 
                v-show="isParentRow || (
                  (!event.nevuaMultipleMarkets || store.tableState.multiMarketEvents[event.id]?.expanded) &&
                  isMarketVisible(event.id, market.id)
                )"
                @click="handleRowClick(event, market, isParentRow)">

              <!-- Title column -->
              <td class="pl-4">
                <div class="flex items-center text-xs" 
                     :class="[!isParentRow && store.tableState.multiMarketEvents[event.id]?.expanded ? 'pl-1 border-l-2 border-gray-300' : '']">
                  <!-- Event title -->
                  <div class="flex flex-col">
                    <span v-html="getEventTitle(event, market, isParentRow)" />
                  </div>
                </div>
              </td>

              <!-- Chance column -->
              <td class="text-center">
                <div v-if="!isParentRow" class="text-left ml-2">
                  <div class="font-normal text-xs">
                    {{ getChance(market) }}
                  </div>
                </div>
                <!-- Chevron for parent rows -->
                <div v-if="isParentRow" class="flex justify-left ml-4">
                  <i :class="[
                      'fa-solid text-gray-500 transition-transform',
                      store.tableState.multiMarketEvents[event.id]?.expanded ? 'fa-chevron-down' : 'fa-chevron-right'
                    ]">
                  </i>
                </div>
              </td>

              <!-- Trend column -->
              <td class="text-center">
                <div v-if="!isParentRow" class="relative">
                  <img 
                    v-if="market.nevuaSVGLink" 
                    :src="`https://img.nevua.markets${market.nevuaSVGLink}`" 
                    class="h-[40px] w-[80px]"
                  />
                </div>
              </td>
            </tr>
             <!-- Add empty row to ensure context menu is visible when scrolled to bottom -->
            <tr class="h-20"></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Subscriptions Dialog -->
    <SubscriptionsDialogContainer
      :is-open="store.showSubscriptionsDialog"
      :is-mobile="true"
      :scope="store.subscriptionScope"
      :target-title="store.subscriptionTargetTitle"
      :current-watchlist="store.currentWatchList"
      :events-to-create-alert-for="store.eventsToCreateAlertFor"
      :subscription-to-edit="store.subscriptionToEdit"
      :list-alerts-for-market-id="store.listAlertsForMarketId"
      :list-alerts-for-event-id="store.listAlertsForEventId"
      :list-alerts-for-watchlist-id="store.listAlertsForWatchlistId"
      :show-alert-list-tab-first="store.showAlertListTabFirst"
      @close="handleCloseSubscriptionsDialog"
      @delete="handleSubscriptionDelete"
      @edit="handleSubscriptionEdit"
      @clear-filters="clearFilters"
    />
  </div>
</template>

<script>
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import { isEmpty, size } from 'lodash';
import SearchBar from './SearchBar.vue';
import { store, handleCreateWatchlistAlert } from '../store';
import SubscriptionsDialogContainer from './SubscriptionsDialogContainer.vue';
import { Dialog, DialogPanel, TransitionRoot, TransitionChild } from '@headlessui/vue';

export default {
  components: {
    SearchBar,
    SubscriptionsDialogContainer,
    Dialog,
    DialogPanel,
    TransitionRoot,
    TransitionChild
  },
  props: {
    marketName: String,
    marketSlug: String,
    loading: Boolean,
    events: Array,
  },
  emits: ['refetch-events-for-watchlist'],
  data() {
    return {
      store,
      animateTowerBroadcast: false,
      searchText: '',
    };
  },
  computed: {
    flattenedEvents() {
      return store.tableState.events.reduce((acc, event) => {
        // Filter markets based on visibility and closed status
        let filteredMarkets = event.markets.filter(market => {
          if (!market.active) {
            console.warn('Found inactive market', market.id, 'in event', event.id);
          }
          return this.isMarketVisible(event.id, market.id) && market.active
        });

        // Skip if all markets are filtered out
        if (filteredMarkets.length === 0) return acc;

        // First check if event title matches search text
        const eventMatchesSearch = this.searchText === '' ||
          event.title.toLowerCase().includes(this.searchText.toLowerCase());

        // If eventMatchesSearch is true, we want to add all markets, so no filtering
        // If it is false, then filter markets based on search text
        filteredMarkets = eventMatchesSearch ? filteredMarkets : filteredMarkets.filter(market =>
          market.question.toLowerCase().includes(this.searchText.toLowerCase())
        );

        if (filteredMarkets.length === 0) {
          return acc;
        }

        // Add an extra parent row for multi-market events
        if (event.nevuaMultipleMarkets) {
          acc.push({
            event: { ...event, markets: filteredMarkets },
            market: filteredMarkets[0],
            isParentRow: true,
            rowKey: this.getRowKey(event, filteredMarkets[0], true)
          });
        }

        // Add individual market rows
        filteredMarkets.forEach(market => {
          acc.push({
            event: { ...event, markets: filteredMarkets },
            market,
            isParentRow: false,
            rowKey: this.getRowKey(event, market, false)
          });
        });

        return acc;
      }, []);
    },
    isMarketVisible() {
      return (eventId, marketId) => {
        if (isEmpty(store.currentWatchList)) {
          return true;
        }
        const watchlistEvent = store.currentWatchList?.events?.[eventId];
        const marketInEvent = watchlistEvent?.markets?.includes(marketId);
        const shouldShowClosed = store.currentWatchList?.tableSettings?.showClosedItems || true;

        if (!watchlistEvent || !marketInEvent) {
          return false;
        }

        return shouldShowClosed;
      }
    },
  },
  methods: {
    refetchEventsForWatchlist(watchListId) {
      this.$emit('refetch-events-for-watchlist', watchListId);
    },
    getRowKey(event, market, isParentRow = false) {
      return `${event.id}-${isParentRow ? 'parent' : market.id}`;
    },
    getEventTitle(event, market, isParentRow) {
      const isEventClosed = event?.closed || event?.active === false;
      const isMarketClosed = market?.closed || market?.active === false;

      if (!event.nevuaMultipleMarkets || !market) {
        if (isEventClosed) {
          return `<span class="text-gray-500">${event.title}</span><span class="text-red-500"> [CLOSED]</span>`;
        }
        return event.title;
      } else {
        const title = isMarketClosed ? 
          `<span class="text-gray-500">${market.question}</span><span class="text-red-500"> [CLOSED]</span>` : 
          market.question;
        
        return title;
      }
    },
    getChance(market) {
      if (!market.outcomePrices) return 'N/A';
      const price = parseFloat(JSON.parse(market.outcomePrices)[0] || 0) * 100;
      return price.toFixed(2) + '%';
    },
    toggleExpanded(eventId) {
      if (store.tableState.multiMarketEvents[eventId]) {
        store.tableState.multiMarketEvents[eventId].expanded = !store.tableState.multiMarketEvents[eventId].expanded;
      }
    },
    handleCloseSubscriptionsDialog() {
      store.showSubscriptionsDialog = false;
      store.subscriptionToEdit = null;
      store.eventsToCreateAlertFor = {};
    },
    handleSubscriptionDelete(subscriptionId) {
      store.subscriptionIdToDelete = subscriptionId;
      store.isSubscriptionDeleteModalOpen = true;
    },
    handleSubscriptionEdit(subscription) {
      store.subscriptionToEdit = subscription;
      store.showSubscriptionsDialog = true;
    },
    clearFilters() {
      this.searchText = '';
    },
    handleRowClick(event, market, isParentRow) {
      if (isParentRow) {
        this.toggleExpanded(event.id);
      } /*else {
        store.showSubscriptionsDialog = true;
        store.subscriptionScope = 'MARKET';
        store.subscriptionTargetTitle = market.question;
        store.eventsToCreateAlertFor = {
          [event.id]: {
            markets: [market.id]
          }
        };
      }*/
    }
  }
};
</script>

<style scoped>
.th-class {
  @apply text-left text-sm font-medium text-gray-500 uppercase;
}
</style> 